import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Screenshot = makeShortcode("Screenshot");
const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`You can `}<a parentName="p" {...{
        "href": "#creating-access-tokens"
      }}>{`create`}</a>{` and `}<a parentName="p" {...{
        "href": "#viewing-access-tokens"
      }}>{`view`}</a>{` access tokens from the website and command line interface (CLI).`}</p>
    <h2>{`Creating access tokens`}</h2>
    <h3>{`Creating tokens on the website`}</h3>
    <ol>
      <li parentName="ol">
        <p parentName="li">{`In the upper right corner of the page, click your profile picture, then click `}<strong parentName="p">{`Access Tokens`}</strong>{`.`}</p>
        <Screenshot src="/integrations/integrating-npm-with-external-services/tokens-profile.png" alt="Screenshot of the account menu with the tokens link selected" mdxType="Screenshot" />
      </li>
      <li parentName="ol">
        <p parentName="li">{`Click `}<strong parentName="p">{`Generate New Token`}</strong>{`.`}</p>
        <Screenshot src="/integrations/integrating-npm-with-external-services/create-token.png" alt="Screenshot of the create new token button" mdxType="Screenshot" />
      </li>
      <li parentName="ol">
        <p parentName="li">{`(Optional) Name your token`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Select the type of access token:`}</p>
        <ul parentName="li">
          <li parentName="ul">
            <p parentName="li"><strong parentName="p">{`Read-only`}</strong>{`: a read-only token can only be used to download packages from the registry.  It will have permission to read any private package that you have access to.  This is recommended for automation and workflows where you are installing packages, but not publishing new ones.`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li"><strong parentName="p">{`Automation`}</strong>{`: an automation token can download packages and publish new ones, but if you have two-factor authentication (2FA) configured on your account, it will `}<strong parentName="p">{`not`}</strong>{` be enforced.  You can use an automation token in continuous integration workflows and other automation systems to publish a package even when you cannot enter a one-time passcode.  This is recommended for automation workflows where you are publishing new packages.`}</p>
          </li>
          <li parentName="ul">
            <p parentName="li"><strong parentName="p">{`Publish`}</strong>{`: a publish token can perform any action on your behalf, including downloading packages, publishing packages, and changing user settings or package settings.  If you have two-factor authentication configured on your account, you will be required to enter a one-time passcode when using a publish token.  This is recommended for interactive workflows.`}</p>
          </li>
        </ul>
        <Screenshot src="/integrations/integrating-npm-with-external-services/token-level-select.png" alt="Screenshot of the access level selection" mdxType="Screenshot" />
      </li>
      <li parentName="ol">
        <p parentName="li">{`Click `}<strong parentName="p">{`Generate Token`}</strong>{`.`}</p>
      </li>
      <li parentName="ol">
        <p parentName="li">{`Copy the token from the top of page.`}</p>
      </li>
    </ol>
    <h3>{`Creating tokens with the CLI`}</h3>
    <p>{`You can create tokens with read-only permissions or read and publish permissions with the CLI; you cannot currently create automation tokens.`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Read-only:`}</strong>{` Tokens that allow installation and distribution only, but no publishing or other rights associated with your account.`}</li>
      <li parentName="ul"><strong parentName="li">{`Publish:`}</strong>{` The default setting for new tokens, and most permissive token type. Publish tokens allow installation, distribution, modification, publishing, and all rights that you have on your account.`}</li>
    </ul>
    <p>{`In addition, you can specify that the token is only valid for a specific IPv4 address range, using `}<a parentName="p" {...{
        "href": "https://en.wikipedia.org/wiki/Classless_Inter-Domain_Routing"
      }}>{`CIDR`}</a>{` notation.  The token will only be valid when used from the specified IP addresses.`}</p>
    <ol>
      <li parentName="ol">{`To create a new token, on the command line, run:`}
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`npm token create`}</inlineCode>{` for a read and publish token`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`npm token create --read-only`}</inlineCode>{` for a read-only token`}</li>
          <li parentName="ul"><inlineCode parentName="li">{`npm token create --cidr=[list]`}</inlineCode>{` for a CIDR-restricted read and publish token. For example, `}<inlineCode parentName="li">{`npm token create --cidr=192.0.2.0/24`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`npm token create --read-only --cidr=[list]`}</inlineCode>{` for a CIDR-restricted read-only token`}</li>
        </ul>
      </li>
      <li parentName="ol">{`When prompted, enter your password.`}</li>
      <li parentName="ol">{`If you have enabled `}<a parentName="li" {...{
          "href": "about-two-factor-authentication"
        }}>{`two-factor authentication`}</a>{`, when prompted, enter a one-time password.`}</li>
      <li parentName="ol">{`Copy the token from the `}<strong parentName="li">{`token`}</strong>{` field in the command output.`}</li>
    </ol>
    <h4>{`CIDR-restricted token errors`}</h4>
    <p>{`If the CIDR string you enter is invalid or in an inappropriate format, you will get an error similar to the one below:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm ERR! CIDR whitelist contains invalid CIDR entry: X.X.X.X./YY,Z.Z.. . .
`}</code></pre>
    <p>{`Make sure you are using a valid IPv4 range and try creating the token again.`}</p>
    <h2>{`Viewing access tokens`}</h2>
    <Note mdxType="Note">
      <p><strong parentName="p">{`Note:`}</strong>{` Full tokens are never displayed, only the first and last four characters will be shown. You can only view a full token immediately after creation.`}</p>
    </Note>
    <h3>{`Viewing tokens on the website`}</h3>
    <p>{`To view all tokens associated with your account, in the upper right corner of the page, click your profile picture, then click `}<strong parentName="p">{`Access Tokens`}</strong>{`.`}</p>
    <Screenshot src="/integrations/integrating-npm-with-external-services/tokens-profile.png" alt="Screenshot of the account menu with the tokens link selected" mdxType="Screenshot" />
    <h3>{`Viewing tokens on the CLI`}</h3>
    <p>{`To view all tokens associated with your account, on the command line, run the following command:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm token list
`}</code></pre>
    <h4>{`Token attributes`}</h4>
    <ul>
      <li parentName="ul"><strong parentName="li">{`id:`}</strong>{` Use the token ID to refer to the token in commands.`}</li>
      <li parentName="ul"><strong parentName="li">{`token:`}</strong>{` The first digits of the actual token.`}</li>
      <li parentName="ul"><strong parentName="li">{`create:`}</strong>{` Date the token was created.`}</li>
      <li parentName="ul"><strong parentName="li">{`readonly:`}</strong>{` If yes, indicates a read-only token. If no, indicates a token with both read and publish permissions.`}</li>
      <li parentName="ul"><strong parentName="li">{`CIDR whitelist:`}</strong>{` Restricts token use by IP address.`}</li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      